import React, { useState, useEffect } from "react";
import {
  getCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getSalespersons,
} from "../../services/apiService";
import "./Customers.css";
import { FaEdit, FaTrash, FaPlus, FaTimes } from "react-icons/fa";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);

  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const emptyForm = {
    customer_number: "",
    customer_name: "",
    area_code: "",
    tin_number: "",
    area_manager_code: "",
    salesperson_id: "",
    lab_contact_person: "",
    accounting_contact_person: "",
    accounting_contact_number: "",
    purchase_contact_person: "",
    purchase_contact_number: "",
    business_style: "",
    email_address: "",
    fax_number: "",
    customer_class: "",
    credit_limit: "",
    terms: "",
    type: "",
    bill_to: "",
    ship_to: "",
    remarks: "",
    created_at: "",
    updated_at: "",
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadCustomers();
    loadSalespersons();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data || []);
    } catch (err) {
      console.error("Load customers error:", err);
    }
  };

  const loadSalespersons = async () => {
    try {
      const data = await getSalespersons();
      setSalespersons(data || []);
    } catch (err) {
      console.error("Load salespersons error:", err);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const normalized =
      typeof value === "string" ? value.replace(" ", "T") : value;

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getCreatedAt = (row) =>
    row?.created_at ||
    row?.createdAt ||
    row?.CreatedAt ||
    row?.date_inputed ||
    row?.date_inputted ||
    row?.DateInputed ||
    row?.DateInputted ||
    row?.date_created ||
    row?.DateCreated;

  const getUpdatedAt = (row) =>
    row?.updated_at ||
    row?.updatedAt ||
    row?.UpdatedAt ||
    row?.date_updated ||
    row?.DateUpdated;

  const openAddModal = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const closeModal = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      const cleanData = {
        ...form,
        credit_limit:
          form.credit_limit === "" ? 0 : parseFloat(form.credit_limit) || 0,
      };

      delete cleanData.created_at;
      delete cleanData.updated_at;
      delete cleanData.createdAt;
      delete cleanData.updatedAt;
      delete cleanData.CreatedAt;
      delete cleanData.UpdatedAt;
      delete cleanData.date_inputed;
      delete cleanData.date_inputted;
      delete cleanData.DateInputed;
      delete cleanData.DateInputted;
      delete cleanData.date_created;
      delete cleanData.DateCreated;
      delete cleanData.date_updated;
      delete cleanData.DateUpdated;

      if (!cleanData.customer_name.trim()) {
        alert("Customer name is required.");
        return;
      }

      if (editId) {
        await updateCustomer(editId, cleanData);
        alert("Customer updated successfully!");
      } else {
        await addCustomer(cleanData);
        alert("Customer added successfully!");
      }

      closeModal();
      await loadCustomers();
      setCurrentPage(1);
    } catch (err) {
      console.error("Save customer error:", err);
      alert("Failed to save customer.");
    }
  };

  const handleEdit = (cust) => {
    setForm({
      ...emptyForm,
      ...cust,
      credit_limit:
        cust.credit_limit !== null && cust.credit_limit !== undefined
          ? cust.credit_limit
          : "",
      created_at: getCreatedAt(cust) || "",
      updated_at: getUpdatedAt(cust) || "",
    });

    setEditId(cust.customer_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;

    try {
      await deleteCustomer(id);
      alert("Customer deleted successfully!");
      await loadCustomers();
    } catch (err) {
      console.error("Delete customer error:", err);
      alert("Failed to delete customer.");
    }
  };

  const areaManagerCodes = [
    "All",
    ...Array.from(
      new Set(customers.map((c) => c.area_manager_code).filter(Boolean))
    ).sort(),
  ];

  const filtered = customers
    .filter((c) =>
      (c.customer_name || "").toLowerCase().includes(search.toLowerCase())
    )
    .filter((c) =>
      areaFilter === "All" ? true : c.area_manager_code === areaFilter
    )
    .sort((a, b) =>
      (a.customer_name || "").localeCompare(b.customer_name || "")
    );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="customers-container">
      <div className="customers-header">
        <h2>CLIENTS</h2>

        <button className="btn-add" onClick={openAddModal} type="button">
          <FaPlus /> Add Client
        </button>
      </div>

      <div className="customers-filters">
        <input
          type="text"
          placeholder="Search client name..."
          className="search-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <select
          value={areaFilter}
          onChange={(e) => {
            setAreaFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          {areaManagerCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>

      <div className="customer-table-wrapper">
        <table className="customer-table">
          <thead>
            <tr>
              <th>Customer Number</th>
              <th>Client Name</th>
              <th>Area Manager Code</th>
              {/* <th>Email</th> */}
              {/* <th>Purchase Contact</th> */}
              <th className="date-col">Date Inputed</th>
              <th className="date-col">Date Updated</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((customer) => (
                <tr key={customer.customer_id || customer.customer_number}>
                  <td title={customer.customer_number}>
                    {customer.customer_number || "-"}
                  </td>

                  <td className="client-name-cell" title={customer.customer_name}>
                    <span className="client-name-text">
                      {customer.customer_name || "-"}
                    </span>
                  </td>

                  <td title={customer.area_manager_code}>
                    {customer.area_manager_code || "-"}
                  </td>
{/* 
                  <td title={customer.email_address}>
                    {customer.email_address || "-"}
                  </td> */}

                  {/* <td title={customer.purchase_contact_person}>
                    {customer.purchase_contact_person || "-"}
                  </td> */}

                  <td
                    className="date-cell"
                    title={formatDateTime(getCreatedAt(customer))}
                  >
                    {formatDateTime(getCreatedAt(customer))}
                  </td>

                  <td
                    className="date-cell"
                    title={formatDateTime(getUpdatedAt(customer))}
                  >
                    {formatDateTime(getUpdatedAt(customer))}
                  </td>

                  <td className="actions-cell">
                    <button
                      className="icon-btn edit"
                      onClick={() => handleEdit(customer)}
                      type="button"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="icon-btn delete"
                      onClick={() => handleDelete(customer.customer_id)}
                      type="button"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(1)}
          type="button"
        >
          First
        </button>

        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          type="button"
        >
          Prev
        </button>

        <span>
          Page {currentPage} of {totalPages}
        </span>

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          type="button"
        >
          Next
        </button>

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
          type="button"
        >
          Last
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content form-modal">
            <div className="modal-header">
              <h3>{editId ? "Edit Customer" : "Add Customer"}</h3>

              <button
                className="modal-close-btn"
                onClick={closeModal}
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            <div className="two-column">
              <div className="col">
                <div className="form-row">
                  <label>Customer Number</label>
                  <input
                    value={form.customer_number}
                    onChange={(e) =>
                      setForm({ ...form, customer_number: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Customer Name</label>
                  <input
                    value={form.customer_name}
                    onChange={(e) =>
                      setForm({ ...form, customer_name: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Area Code</label>
                  <input
                    value={form.area_code}
                    onChange={(e) =>
                      setForm({ ...form, area_code: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>TIN Number</label>
                  <input
                    value={form.tin_number}
                    onChange={(e) =>
                      setForm({ ...form, tin_number: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Area Manager</label>
                  <select
                    value={
                      form.salesperson_id || form.area_manager_code
                        ? JSON.stringify({
                            id: form.salesperson_id || "",
                            code: form.area_manager_code || "",
                          })
                        : ""
                    }
                    onChange={(e) => {
                      if (!e.target.value) {
                        setForm({
                          ...form,
                          salesperson_id: "",
                          area_manager_code: "",
                        });
                        return;
                      }

                      const selected = JSON.parse(e.target.value);

                      setForm({
                        ...form,
                        salesperson_id: selected.id,
                        area_manager_code: selected.code,
                      });
                    }}
                  >
                    <option value="">-- Select Area Manager --</option>

                    {salespersons.map((sp) => (
                      <option
                        key={sp.salesperson_id}
                        value={JSON.stringify({
                          id: sp.salesperson_id,
                          code: sp.area_manager_code,
                        })}
                      >
                        {sp.area_manager_code} — {sp.salesperson_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label>Lab Contact Person</label>
                  <input
                    value={form.lab_contact_person}
                    onChange={(e) =>
                      setForm({ ...form, lab_contact_person: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Accounting Contact Person</label>
                  <input
                    value={form.accounting_contact_person}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        accounting_contact_person: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Accounting Contact Number</label>
                  <input
                    value={form.accounting_contact_number}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        accounting_contact_number: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Purchase Contact Person</label>
                  <input
                    value={form.purchase_contact_person}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        purchase_contact_person: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Purchase Contact Number</label>
                  <input
                    value={form.purchase_contact_number}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        purchase_contact_number: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="col">
                <div className="form-row">
                  <label>Business Style</label>
                  <input
                    value={form.business_style}
                    onChange={(e) =>
                      setForm({ ...form, business_style: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Email Address</label>
                  <input
                    value={form.email_address}
                    onChange={(e) =>
                      setForm({ ...form, email_address: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Fax Number</label>
                  <input
                    value={form.fax_number}
                    onChange={(e) =>
                      setForm({ ...form, fax_number: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Customer Class</label>
                  <input
                    value={form.customer_class}
                    onChange={(e) =>
                      setForm({ ...form, customer_class: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Credit Limit</label>
                  <input
                    type="number"
                    value={form.credit_limit}
                    onChange={(e) =>
                      setForm({ ...form, credit_limit: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Terms</label>
                  <input
                    value={form.terms}
                    onChange={(e) =>
                      setForm({ ...form, terms: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Type</label>
                  <input
                    value={form.type}
                    onChange={(e) =>
                      setForm({ ...form, type: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Bill To</label>
                  <textarea
                    value={form.bill_to}
                    onChange={(e) =>
                      setForm({ ...form, bill_to: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Ship To</label>
                  <textarea
                    value={form.ship_to}
                    onChange={(e) =>
                      setForm({ ...form, ship_to: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Remarks</label>
                  <textarea
                    value={form.remarks}
                    onChange={(e) =>
                      setForm({ ...form, remarks: e.target.value })
                    }
                  />
                </div>

                {editId && (
                  <>
                    <div className="form-row">
                      <label>Date Inputed</label>
                      <input value={formatDateTime(form.created_at)} readOnly />
                    </div>

                    <div className="form-row">
                      <label>Date Updated</label>
                      <input value={formatDateTime(form.updated_at)} readOnly />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-save" onClick={handleSave} type="button">
                {editId ? "Update Customer" : "Save Customer"}
              </button>

              <button className="btn-cancel" onClick={closeModal} type="button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}